/**
 * SmallMultiplesGrid
 *
 * Tufte: small multiples = same chart repeated for direct visual comparison.
 * Tiled cards, one per leg, sized identical so the eye scans the slate.
 * Per card: player + sparkline + the 2-3 stats that matter for that stat type.
 *
 * Props:
 *   legs:        array of leg rows (same shape as SuggestedParlays gets)
 *   sparklines:  map { "player_id:stat_type" -> [{game_date, actual, hit}] }
 *   sport:       string
 */

import React, { useMemo, useState } from 'react';
import Sparkline from './Sparkline';

const STAT_GROUPS = [
  { id: 'all',     label: 'All' },
  { id: 'hits',    label: 'Hits',     match: ['hits'] },
  { id: 'hr',      label: 'HR',       match: ['home_runs'] },
  { id: 'pitcher', label: 'Pitcher',  match: ['pitcher_strikeouts', 'pitching_outs'] },
  { id: 'other',   label: 'Other',    match: ['runs', 'rbis', 'total_bases', 'stolen_bases'] },
];

const STAT_LABEL = {
  hits: 'Hits',
  runs: 'Runs',
  rbis: 'RBI',
  home_runs: 'HR',
  total_bases: 'TB',
  stolen_bases: 'SB',
  pitcher_strikeouts: 'K',
  pitching_outs: 'Outs',
};

const TIER_RING = {
  PRIME:  'ring-purple-500/60',
  STRONG: 'ring-blue-500/60',
  SOLID:  'ring-green-500/60',
  LOCK:   'ring-orange-500/60',
};

function matchupShort(m) {
  if (!m) return '';
  for (const sep of [' @ ', ' vs ']) {
    if (m.includes(sep)) {
      const [a, h] = m.split(sep);
      return `${a.split(' ').slice(-1)[0]}/${h.split(' ').slice(-1)[0]}`;
    }
  }
  return m.slice(0, 14);
}

// Tiers we never surface — SOLID and FADE are noise for the slate.
const DROP_TIERS = new Set(['SOLID', 'FADE']);

export default function SmallMultiplesGrid({ legs = [], sparklines = {} }) {
  const [tab, setTab] = useState('all');

  const visible = useMemo(() => {
    const g = STAT_GROUPS.find((s) => s.id === tab);
    const allow = g?.match;
    let filtered = allow ? legs.filter((l) => allow.includes(l.stat_type)) : legs;
    // Drop SOLID/FADE everywhere; keep any leg that still has a hit rate so the
    // "All" tab spans every stat type (not just the tiered HR legs).
    filtered = filtered.filter((l) => {
      const tier = l.quality_tier || l.legacy_tier;
      if (tier && DROP_TIERS.has(tier)) return false;
      return l.prob !== null && l.prob !== undefined;
    });

    if (tab !== 'all') {
      return [...filtered].sort((a, b) => (b.prob ?? -1) - (a.prob ?? -1)).slice(0, 12);
    }

    // "All" = a COMBO: round-robin the best of hits / HR / pitcher / other so the
    // glance shows a MIX of stat types, not just the high-prob hits. 12 = 3 rows.
    const groups = { hits: [], hr: [], pitcher: [], other: [] };
    for (const l of filtered) {
      if (l.stat_type === 'hits') groups.hits.push(l);
      else if (l.stat_type === 'home_runs') groups.hr.push(l);
      else if (['pitcher_strikeouts', 'pitching_outs'].includes(l.stat_type)) groups.pitcher.push(l);
      else groups.other.push(l);
    }
    Object.values(groups).forEach((arr) => arr.sort((a, b) => (b.prob ?? -1) - (a.prob ?? -1)));
    const order = ['hits', 'hr', 'pitcher', 'other'];
    const out = [];
    let progressed = true;
    while (out.length < 12 && progressed) {
      progressed = false;
      for (const k of order) {
        if (groups[k].length) {
          out.push(groups[k].shift());
          progressed = true;
          if (out.length >= 12) break;
        }
      }
    }
    return out;
  }, [legs, tab]);

  if (!legs.length) return null;

  return (
    <div className="glass-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-text-primary">
          Slate at a glance
          <span className="text-xs text-text-secondary ml-2 font-normal">
            {tab === 'all' ? 'best across hits / HR / pitcher' : `top ${visible.length} by hit rate`} · last 10 ·{' '}
            <span className="text-text-secondary">– – line</span>,{' '}
            <span className="text-amber-400">— avg</span>
          </span>
        </h3>
        <div className="flex gap-1 text-xs">
          {STAT_GROUPS.map((g) => (
            <button
              key={g.id}
              onClick={() => setTab(g.id)}
              className={
                'px-2 py-1 rounded transition ' +
                (tab === g.id
                  ? 'bg-accent-blue/20 text-accent-blue font-semibold'
                  : 'text-text-secondary hover:text-text-primary')
              }
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="text-sm text-text-secondary text-center py-6">
          No picks in this category today.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {visible.map((l) => {
            const key = `${l.player_id}:${l.stat_type}`;
            const points = sparklines[key] || [];
            const tier = l.quality_tier || l.legacy_tier;
            const ring = TIER_RING[tier] || 'ring-border';
            const statLabel = STAT_LABEL[l.stat_type] || l.stat_type;

            // Direct labels: hit count over the line, season avg, recent trend.
            const shown = points.slice(-10);
            const hitN = shown.filter((p) => p.hit).length;
            // Flame only when genuinely hot, so it stays signal not decoration.
            const hot = l.streak_hit_rate != null && l.streak_hit_rate >= 70
              && l.streak_games_window >= 5;
            const rRec = l.recent_rate_per_10ab;
            const rSeas = l.rate_per_10ab;
            let trend = null;
            if (rRec != null && rSeas != null && rSeas > 0) {
              const up = rRec >= rSeas;
              const pct = Math.abs((100 * (rRec - rSeas)) / rSeas).toFixed(0);
              trend = (
                <span className={up ? 'text-accent-green' : 'text-red-400'}>
                  {up ? '▲' : '▼'}{pct}%
                </span>
              );
            }

            return (
              <div
                key={l.id}
                className={`bg-bg-secondary rounded-md p-2.5 ring-1 ${ring}`}
              >
                <div className="flex items-baseline justify-between gap-1 mb-0.5">
                  <span className="text-sm font-medium text-text-primary truncate">
                    {l.player_name}
                  </span>
                  <span className="flex items-baseline gap-1.5 shrink-0">
                    {hot && (
                      <span
                        className="text-[10px] text-orange-400"
                        title={`Hot: ${l.streak_games_hit}/${l.streak_games_window} games (${Math.round(l.streak_hit_rate)}%)`}
                      >
                        🔥{l.streak_games_hit}
                      </span>
                    )}
                    <span className="text-sm font-bold text-text-primary">
                      {l.prob != null ? `${l.prob.toFixed(0)}%` : '—'}
                    </span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-1 text-xs mb-1.5">
                  <span className="text-text-secondary truncate">
                    {matchupShort(l.matchup)}{' '}
                    <span className="text-accent-blue">{statLabel} {l.sb_line}</span>
                  </span>
                  <span className="text-[9px] uppercase tracking-wide text-text-secondary shrink-0">
                    {tier}
                  </span>
                </div>
                <Sparkline
                  points={points}
                  line={l.sb_line}
                  avg={l.season_avg}
                  width={132}
                  height={30}
                  count={10}
                />
                <div className="flex items-center justify-between text-[10px] mt-1 text-text-secondary">
                  <span>
                    <span className="font-semibold text-text-primary">{hitN}/{shown.length}</span> over
                  </span>
                  {l.season_avg != null && (
                    <span>
                      avg <span className="text-amber-400 font-medium">
                        {l.season_avg >= 10 ? l.season_avg.toFixed(0)
                          : l.season_avg < 1 ? l.season_avg.toFixed(2)
                          : l.season_avg.toFixed(1)}
                      </span>
                    </span>
                  )}
                  {trend}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
